import { List, fromJS } from 'immutable';

import {
  CLEAR_CHARGES,
  START_LOADING,
  GET_FLEET_SETTINGS_SUCCESS,
  GET_FLEET_SETTINGS_FAIL,
  GET_FLEET_SETTINGS_ALERTS_SUCCESS,
  GET_FLEET_SETTINGS_ALERTS_FAIL,
  GET_TERMS_AND_CONDITIONS_SUCCESS,
  GET_TERMS_AND_CONDITIONS_FAIL,
  CREATE_TERMS_AND_CONDITIONS_SUCCESS,
  CREATE_TERMS_AND_CONDITIONS_FAIL,
  UPDATE_TERMS_AND_CONDITIONS_SUCCESS,
  UPDATE_TERMS_AND_CONDITIONS_FAIL,
  UPDATE_FLEET_SETTINGS_ALERTS_SUCCESS,
  UPDATE_FLEET_SETTINGS_ALERTS_FAIL,
  UPDATE_FLEET_SETTINGS_SUCCESS,
  UPDATE_FLEET_SETTINGS_FAIL,
  GET_CHARGES_SUCCESS,
  GET_CHARGES_FAIL,
  GET_DEFAULT_CHARGES_SUCCESS,
  GET_DEFAULT_CHARGES_FAIL,
  ADD_CHARGE_SUCCESS,
  ADD_CHARGE_FAIL,
  EDIT_CHARGE_SUCCESS,
  EDIT_CHARGE_FAIL,
  DELETE_CHARGE_SUCCESS,
  DELETE_CHARGE_FAIL,
  GET_TAXES_SUCCESS,
  GET_TAXES_FAIL,
  ADD_TAX_SUCCESS,
  ADD_TAX_FAIL,
  UPDATE_TAX_SUCCESS,
  UPDATE_TAX_FAIL,
  DELETE_TAX_SUCCESS,
  DELETE_TAX_FAIL,
  GET_DEVICES_SUCCESS,
  GET_DEVICES_FAIL,
  DISCONNECT_DEVICE_SUCCESS,
  DISCONNECT_DEVICE_FAIL,
  FORCE_VIN_SUCCESS,
  FORCE_VIN_FAIL,
} from './actions';

import { GENERIC_ERROR } from 'constants/errors.constants';
import {
  AlertType,
  alertFactory,
  defaultChargeFactory,
  devicesFactory,
  settingsFactory,
  settingsStateFactory,
} from './factories';

const initialState = settingsStateFactory();

function sanitizeAlerts(alerts): AlertType {
  return {
    id: alerts.id,
    turnBackMiles: alerts.turnBackMiles !== null ? alerts.turnBackMiles : '',
    highRpmThreshold: alerts.highRpmThreshold !== null ? alerts.highRpmThreshold : '',
    highSpeedThreshold: alerts.highSpeedThreshold !== null ? alerts.highSpeedThreshold : '',
    batteryLow: alerts.batteryLow !== null ? Number(alerts.batteryLow) : '',
    defaultDayOut: alerts.defaultDayOut !== null ? alerts.defaultDayOut : '',
  };
}

function fleetSettingsServiceReducer(state = initialState, action) {
  switch (action.type) {
    case START_LOADING:
      return state.set('loading', true);

    case CLEAR_CHARGES:
      return state.set('charges', List()).set('defaultCharges', List());
    case GET_FLEET_SETTINGS_SUCCESS:
      return state
        .set('settings', settingsFactory(action.payload.settings))
        .set('loading', false)
        .set('errors', fromJS({}));
    case GET_FLEET_SETTINGS_FAIL:
      return state
        .set('settings', settingsFactory({}))
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_FLEET_SETTINGS_SUCCESS:
      return state
        .set('settings', settingsFactory(action.payload.settings))
        .set('loading', false)
        .set('errors', fromJS({}));
    case UPDATE_FLEET_SETTINGS_FAIL:
      return state
        .set('settings', settingsFactory({}))
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_FLEET_SETTINGS_ALERTS_SUCCESS:
      return state
        .set('alerts', alertFactory(sanitizeAlerts(action.payload.alerts)))
        .set('loading', false)
        .set('errors', fromJS({}));
    case GET_FLEET_SETTINGS_ALERTS_FAIL:
      return state
        .set('alerts', alertFactory())
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_FLEET_SETTINGS_ALERTS_SUCCESS:
      return state
        .set('alerts', alertFactory(sanitizeAlerts(action.payload.alerts)))
        .set('loading', false)
        .set('errors', fromJS({}));
    case UPDATE_FLEET_SETTINGS_ALERTS_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_TERMS_AND_CONDITIONS_SUCCESS:
      return state
        .set('termsAndConditions', fromJS(action.payload.termsAndConditions))
        .set('loading', false)
        .set('errors', fromJS({}));
    case GET_TERMS_AND_CONDITIONS_FAIL:
      return state
        .set('termsAndConditions', fromJS({}))
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case CREATE_TERMS_AND_CONDITIONS_SUCCESS:
      return state
        .set('termsAndConditions', fromJS(action.payload.termsAndConditions))
        .set('loading', false)
        .set('errors', fromJS({}));
    case CREATE_TERMS_AND_CONDITIONS_FAIL:
      return state
        .set('termsAndConditions', fromJS({}))
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_TERMS_AND_CONDITIONS_SUCCESS:
      return state
        .set('termsAndConditions', fromJS(action.payload.termsAndConditions))
        .set('loading', false)
        .set('errors', fromJS({}));
    case UPDATE_TERMS_AND_CONDITIONS_FAIL:
      return state
        .set('termsAndConditions', fromJS({}))
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_CHARGES_SUCCESS: {
      const charges = action.payload.data.results.map((charge) => defaultChargeFactory(charge));

      return state.set('charges', List(charges)).set('loading', false).set('chargesErrors', fromJS({}));
    }
    case GET_CHARGES_FAIL:
      return state
        .set('charges', List())
        .set('loading', false)
        .set('chargesErrors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_DEFAULT_CHARGES_SUCCESS: {
      const defaultCharges = action.payload.defaultCharges.map((charge) => defaultChargeFactory(charge));
      return state.set('defaultCharges', List(defaultCharges)).set('loading', false);
    }
    case GET_DEFAULT_CHARGES_FAIL:
      return state.set('defaultCharges', List()).set('loading', false);
    case ADD_CHARGE_SUCCESS: {
      const { charge } = action.payload;

      return state
        .set('charges', state.get('charges').push(defaultChargeFactory(charge)))
        .set('loading', false)
        .set('chargesErrors', fromJS({}));
    }
    case ADD_CHARGE_FAIL:
      return state.set('loading', false).set('chargesErrors', fromJS(action.payload.errors || GENERIC_ERROR));
    case EDIT_CHARGE_SUCCESS: {
      const { id, charge: updatedCharge } = action.payload;

      const charges = state.get('charges');

      const newCharges = charges.map((obj) => {
        if (obj.get('id') === id) {
          // Replace the existing object with the new one
          return defaultChargeFactory(updatedCharge);
        }
        return obj;
      });

      return state.set('charges', List(newCharges)).set('loading', false).set('chargesErrors', fromJS({}));
    }
    case EDIT_CHARGE_FAIL:
      return state.set('loading', false).set('chargesErrors', fromJS(action.payload.errors || GENERIC_ERROR));
    case DELETE_CHARGE_SUCCESS: {
      const index = state.get('charges').findIndex((charge) => charge.get('id') === action.payload.id);
      return state
        .set('charges', state.get('charges').delete(index))
        .set('loading', false)
        .set('chargesErrors', fromJS({}));
    }
    case DELETE_CHARGE_FAIL:
      return state.set('loading', false).set('chargesErrors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_TAXES_SUCCESS:
      return state
        .set('taxes', fromJS(action.payload.data.results))
        .set('loading', false)
        .set('taxesErrors', fromJS({}));
    case GET_TAXES_FAIL:
      return state
        .set('taxes', List())
        .set('loading', false)
        .set('taxesErrors', fromJS(action.payload.errors || GENERIC_ERROR));
    case ADD_TAX_SUCCESS:
      return state
        .set('taxes', state.get('taxes').push(fromJS(action.payload.tax)))
        .set('loading', false)
        .set('taxesErrors', fromJS({}));
    case ADD_TAX_FAIL:
      return state.set('loading', false).set('taxesErrors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_TAX_SUCCESS: {
      const { id } = action.payload.tax;

      return (
        state
          // Find the existing tax by id, and swap in the new one
          .update('taxes', (taxes) => taxes.map((tax) => (tax.get('id') === id ? fromJS(action.payload.tax) : tax)))
          .set('loading', false)
          .set('taxesErrors', fromJS({}))
      );
    }
    case UPDATE_TAX_FAIL:
      return state.set('loading', false).set('taxesErrors', fromJS(action.payload.errors || GENERIC_ERROR));
    case DELETE_TAX_SUCCESS: {
      const index = state.get('taxes').findIndex((charge) => charge.get('id') === action.payload.id);

      return state.set('taxes', state.get('taxes').delete(index)).set('loading', false).set('taxesErrors', fromJS({}));
    }
    case DELETE_TAX_FAIL:
      return state.set('loading', false).set('taxesErrors', fromJS(action.payload.errors || GENERIC_ERROR));

    case GET_DEVICES_SUCCESS:
      return state
        .set('devices', List(action.payload.data.map((device) => devicesFactory(device))))
        .set('loading', false)
        .set('errors', fromJS({}));
    case GET_DEVICES_FAIL:
      return state
        .set('devices', List())
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case FORCE_VIN_SUCCESS:
    case DISCONNECT_DEVICE_SUCCESS: {
      const { id } = action.payload.data;

      const currentDevices = state.get('devices', List());
      const deviceIndex = currentDevices.findIndex((device) => device.get('id') === id);

      if (deviceIndex === -1) {
        const devices = currentDevices.push(devicesFactory(action.payload.data));
        return state.set('devices', devices).set('loading', false);
      }

      return state
        .update('devices', (devices) =>
          devices.map((device) => (device.get('id') === id ? devicesFactory(action.payload.data) : device))
        )
        .set('loading', false);
    }
    case FORCE_VIN_FAIL:
    case DISCONNECT_DEVICE_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    default:
      return state;
  }
}

export default fleetSettingsServiceReducer;
